import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Users, Settings, Plus, Edit, Trash2, Download, Upload, 
  Building2, Home, MapPin, Wrench, AlertTriangle, ChevronRight,
  Save, X, FileSpreadsheet, MessageCircle, Send, Eye, EyeOff, Info, CheckCircle2,
  Loader2, Bell, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  supervisoresAPI, proyectosAPI, subdivisionesAPI, 
  catalogosAPI, asignacionesAPI, configuracionGeneralAPI 
} from '../utils/api';
import { useAuth } from '../context/AuthContext';

// Componente para gestionar un catálogo genérico con soporte para relaciones padre
const CatalogoManager = ({ 
  title, 
  icon: Icon,
  items, 
  onAdd, 
  onEdit, 
  onDelete,
  onDeleteAll, // Nueva prop para eliminar todos
  onToggleActivo,
  onImport, 
  onExport,
  loading,
  // Nuevas props para relación padre
  parentField = null, // Campo que almacena el nombre del padre (ej: 'categoria_nombre')
  parentLabel = null, // Etiqueta para mostrar (ej: 'Categoría')
  parentOptions = []  // Lista de opciones del padre [{nombre: 'xxx'}, ...]
}) => {
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false); // Modal confirmación
  const [editItem, setEditItem] = useState(null);
  const [newNombre, setNewNombre] = useState('');
  const [newDescripcion, setNewDescripcion] = useState('');
  const [newActivo, setNewActivo] = useState(true);
  const [newParent, setNewParent] = useState('none'); // Valor del padre seleccionado ('none' = sin asignar)
  const [deletingAll, setDeletingAll] = useState(false); // Estado de carga al eliminar
  const [exporting, setExporting] = useState(false); // Estado de carga al exportar
  const [importing, setImporting] = useState(false); // Estado de carga al importar
  const fileInputRef = useRef(null);

  const handleDeleteAll = async () => {
    if (!onDeleteAll) return;
    setDeletingAll(true);
    try {
      await onDeleteAll();
      setShowDeleteAllConfirm(false);
    } finally {
      setDeletingAll(false);
    }
  };

  const handleAdd = () => {
    if (!newNombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    const data = { nombre: newNombre.trim(), descripcion: newDescripcion.trim(), activo: newActivo };
    if (parentField) {
      // Convert "none" back to null for the API
      data[parentField] = newParent === 'none' ? null : newParent || null;
    }
    onAdd(data);
    setNewNombre('');
    setNewDescripcion('');
    setNewActivo(true);
    setNewParent('none');
    setShowAdd(false);
  };

  const handleEdit = () => {
    if (!newNombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    const data = { nombre: newNombre.trim(), descripcion: newDescripcion.trim(), activo: newActivo };
    if (parentField) {
      // Convert "none" back to null for the API
      data[parentField] = newParent === 'none' ? null : newParent || null;
    }
    onEdit(editItem.id, data);
    setEditItem(null);
    setShowEdit(false);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setNewNombre(item.nombre);
    setNewDescripcion(item.descripcion || '');
    setNewActivo(item.activo !== false);
    // Use 'none' for empty values
    setNewParent(parentField ? (item[parentField] || 'none') : 'none');
    setShowEdit(true);
  };

  const openAdd = () => {
    setNewNombre('');
    setNewDescripcion('');
    setNewActivo(true);
    setNewParent('none');
    setShowAdd(true);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImporting(true);
      try {
        await onImport(file);
      } finally {
        setImporting(false);
        e.target.value = '';
      }
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await onExport();
    } finally {
      setExporting(false);
    }
  };

  // Agrupar items por padre si hay relación
  const groupedItems = parentField ? items.reduce((acc, item) => {
    const parent = item[parentField] || 'Sin asignar';
    if (!acc[parent]) acc[parent] = [];
    acc[parent].push(item);
    return acc;
  }, {}) : null;

  return (
    <Card className="bg-white border border-[#E8E8E7] shadow-sm">
      <CardHeader className="pb-3 px-3 sm:px-6">
        <div className="flex flex-col gap-2">
          {/* Título y badge */}
          <div className="flex items-center gap-2">
            <Icon size={18} className="text-[#9d8272] flex-shrink-0" />
            <CardTitle className="text-sm sm:text-base font-semibold truncate">{title}</CardTitle>
            <Badge variant="secondary" className="ml-auto sm:ml-2 flex-shrink-0">{items.length}</Badge>
          </div>
          {/* Botones de acción - responsivos */}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleExport}
              disabled={exporting}
              className="text-xs flex-1 sm:flex-none min-w-[80px]"
              data-testid={`export-${title.toLowerCase()}`}
            >
              {exporting ? (
                <Loader2 size={14} className="mr-1 animate-spin" />
              ) : (
                <Download size={14} className="mr-1" />
              )}
              <span className="hidden xs:inline">{exporting ? 'Exportando...' : 'Exportar'}</span>
              <span className="xs:hidden">{exporting ? '...' : 'Exp.'}</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="text-xs flex-1 sm:flex-none min-w-[80px]"
              data-testid={`import-${title.toLowerCase()}`}
            >
              {importing ? (
                <Loader2 size={14} className="mr-1 animate-spin" />
              ) : (
                <Upload size={14} className="mr-1" />
              )}
              <span className="hidden xs:inline">{importing ? 'Importando...' : 'Importar'}</span>
              <span className="xs:hidden">{importing ? '...' : 'Imp.'}</span>
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".xlsx,.xls"
              className="hidden"
              disabled={importing}
            />
            {onDeleteAll && items.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowDeleteAllConfirm(true)}
                className="text-xs flex-1 sm:flex-none min-w-[80px] text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                data-testid={`delete-all-${title.toLowerCase()}`}
              >
                <Trash2 size={14} className="mr-1" />
                <span className="hidden xs:inline">Eliminar Todo</span>
                <span className="xs:hidden">Elim.</span>
              </Button>
            )}
            <Button
              size="sm"
              onClick={openAdd}
              className="bg-[#9d8272] hover:bg-[#8a7162] text-white text-xs flex-1 sm:flex-none min-w-[80px]"
              data-testid={`add-${title.toLowerCase()}`}
            >
              <Plus size={14} className="mr-1" />
              Agregar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-3 sm:px-6">
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9d8272]"></div>
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#888888] text-center py-4">No hay elementos registrados</p>
        ) : (
          <div className="max-h-48 overflow-x-auto overflow-y-auto">
            <table className="w-full text-xs sm:text-sm min-w-[400px]">
              <thead className="bg-[#F5F5F5] sticky top-0">
                <tr>
                  <th className="text-left p-1.5 sm:p-2 font-medium">Nombre</th>
                  {parentField && (
                    <th className="text-left p-1.5 sm:p-2 font-medium hidden md:table-cell">{parentLabel}</th>
                  )}
                  <th className="text-left p-1.5 sm:p-2 font-medium hidden sm:table-cell">Descripción</th>
                  <th className="text-center p-1.5 sm:p-2 font-medium w-16 sm:w-20">Activo</th>
                  <th className="text-right p-1.5 sm:p-2 font-medium w-20 sm:w-24">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className={`border-t border-[#E8E8E7] hover:bg-[#FAFAFA] ${item.activo === false ? 'opacity-50' : ''}`}>
                    <td className="p-1.5 sm:p-2 font-medium">
                      <div className="truncate max-w-[120px] sm:max-w-none">{item.nombre}</div>
                      {parentField && item[parentField] && (
                        <div className="text-[10px] text-[#9d8272] md:hidden">
                          {parentLabel}: {item[parentField]}
                        </div>
                      )}
                      <div className="text-[10px] text-[#888888] sm:hidden truncate">{item.descripcion || '-'}</div>
                    </td>
                    {parentField && (
                      <td className="p-1.5 sm:p-2 text-[#888888] hidden md:table-cell">
                        {item[parentField] ? (
                          <Badge variant="outline" className="text-[10px] bg-[#f5f0eb] border-[#c5b8a2]">
                            {item[parentField]}
                          </Badge>
                        ) : (
                          <span className="text-[#aaa]">-</span>
                        )}
                      </td>
                    )}
                    <td className="p-1.5 sm:p-2 text-[#888888] hidden sm:table-cell">
                      <div className="truncate max-w-[150px]">{item.descripcion || '-'}</div>
                    </td>
                    <td className="p-1.5 sm:p-2 text-center">
                      <button
                        onClick={() => onToggleActivo(item.id, !item.activo)}
                        className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium transition-colors ${
                          item.activo !== false
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        {item.activo !== false ? 'TRUE' : 'FALSE'}
                      </button>
                    </td>
                    <td className="p-1.5 sm:p-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(item)}
                        className="h-6 w-6 sm:h-7 sm:w-7 p-0 mr-0.5 sm:mr-1"
                      >
                        <Edit size={12} className="sm:w-[14px] sm:h-[14px] text-[#9d8272]" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDelete(item.id)}
                        className="h-6 w-6 sm:h-7 sm:w-7 p-0 text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={12} className="sm:w-[14px] sm:h-[14px]" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Dialog Agregar */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar {title.slice(0, -1)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {parentField && parentOptions.length > 0 && (
              <div>
                <Label>{parentLabel} (opcional)</Label>
                <Select value={newParent} onValueChange={setNewParent}>
                  <SelectTrigger className="border-[#c5b8a2]">
                    <SelectValue placeholder={`Seleccionar ${parentLabel?.toLowerCase() || 'padre'}`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {parentOptions.filter(p => p.activo !== false).map((opt) => (
                      <SelectItem key={opt.id || opt.nombre} value={opt.nombre}>
                        {opt.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Nombre *</Label>
              <Input
                value={newNombre}
                onChange={(e) => setNewNombre(e.target.value)}
                placeholder="Nombre del elemento"
                className="border-[#c5b8a2]"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Input
                value={newDescripcion}
                onChange={(e) => setNewDescripcion(e.target.value)}
                placeholder="Descripción opcional"
                className="border-[#c5b8a2]"
              />
            </div>
            <div className="flex items-center gap-3">
              <Label>Activo</Label>
              <button
                type="button"
                onClick={() => setNewActivo(!newActivo)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  newActivo
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                {newActivo ? 'TRUE' : 'FALSE'}
              </button>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancelar</Button>
              <Button onClick={handleAdd} className="bg-[#9d8272] hover:bg-[#8a7162] text-white">
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar {title.slice(0, -1)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {parentField && parentOptions.length > 0 && (
              <div>
                <Label>{parentLabel} (opcional)</Label>
                <Select value={newParent} onValueChange={setNewParent}>
                  <SelectTrigger className="border-[#c5b8a2]">
                    <SelectValue placeholder={`Seleccionar ${parentLabel?.toLowerCase() || 'padre'}`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {parentOptions.filter(p => p.activo !== false).map((opt) => (
                      <SelectItem key={opt.id || opt.nombre} value={opt.nombre}>
                        {opt.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Nombre *</Label>
              <Input
                value={newNombre}
                onChange={(e) => setNewNombre(e.target.value)}
                className="border-[#c5b8a2]"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Input
                value={newDescripcion}
                onChange={(e) => setNewDescripcion(e.target.value)}
                className="border-[#c5b8a2]"
              />
            </div>
            <div className="flex items-center gap-3">
              <Label>Activo</Label>
              <button
                type="button"
                onClick={() => setNewActivo(!newActivo)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  newActivo
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                {newActivo ? 'TRUE' : 'FALSE'}
              </button>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEdit(false)}>Cancelar</Button>
              <Button onClick={handleEdit} className="bg-[#9d8272] hover:bg-[#8a7162] text-white">
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmación para Eliminar Todo */}
      <Dialog open={showDeleteAllConfirm} onOpenChange={setShowDeleteAllConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Confirmar eliminación
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-[#666666] mb-4">
              ¿Estás seguro de que deseas eliminar <strong>todos los elementos</strong> de "{title}"?
            </p>
            <p className="text-sm text-[#666666] mb-2">
              Se eliminarán <strong className="text-red-600">{items.length} elementos</strong> de forma permanente.
            </p>
            <p className="text-xs text-red-500 bg-red-50 p-2 rounded">
              ⚠️ Esta acción no se puede deshacer.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteAllConfirm(false)}
              disabled={deletingAll}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAll}
              disabled={deletingAll}
              className="bg-red-600 hover:bg-red-700"
              data-testid={`confirm-delete-all-${title.toLowerCase()}`}
            >
              {deletingAll ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 size={14} className="mr-1" />
                  Sí, eliminar todo
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const Configuracion = () => {
  // Obtener usuario actual para control de acceso
  const { user } = useAuth();
  const isAdmin = user?.rol_nombre === 'Administrador';
  
  // Estados para supervisores y proyectos
  const [supervisores, setSupervisores] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [subdivisiones, setSubdivisiones] = useState({});
  const [asignaciones, setAsignaciones] = useState([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [showAsignar, setShowAsignar] = useState(false);
  
  // Estados para tabla de asignaciones
  const [tablaAsignaciones, setTablaAsignaciones] = useState([]);
  const [loadingAsignaciones, setLoadingAsignaciones] = useState(true);
  const [tablaModificada, setTablaModificada] = useState(false);
  const [savingTabla, setSavingTabla] = useState(false);
  
  // Estados para import/export de asignaciones
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Estados para catálogos
  const [categorias, setCategorias] = useState([]);
  const [lugares, setLugares] = useState([]);
  const [items, setItems] = useState([]);
  const [fallas, setFallas] = useState([]);
  
  // Estados para configuración NPS
  const [npsConfig, setNpsConfig] = useState({
    nps_habilitado: false,
    nps_url_token: '',
    nps_cliente_id: '',
    nps_secret_id: '',
    nps_url_whatsapp: '',
    nps_bot_id: '',
    nps_template: '',
    templates_whatsapp: [],
    aprobar_acta_inspeccion_whatsapp: false,
    aprobar_acta_atencion_whatsapp: false,
    aprobar_acta_diaria_whatsapp: false
  });
  const [showSecrets, setShowSecrets] = useState(false);
  const [savingNps, setSavingNps] = useState(false);
  
  // Estado para configuración general
  const [generalConfig, setGeneralConfig] = useState({
    limite_reprogramaciones: 3,
    primera_respuesta_autoagendamiento_activo: false,
    recordatorio_autoagendamiento_activo: false,
    limite_recordatorios_autoagendamientos: 3,
    api_callback_url: '',
    daily_notifications_activo: false,
    stand_by_check_activo: false
  });
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savingNotifInternas, setSavingNotifInternas] = useState(false);

  // Estado para configuración de notificaciones a contratistas
  const [notificacionesConfig, setNotificacionesConfig] = useState({
    notificar_contratistas_auto: false
  });
  const [savingNotificaciones, setSavingNotificaciones] = useState(false);
  
  // Verificar si los campos NPS están configurados (para control de acceso del switch)
  const camposNpsConfigurados = Boolean(
    npsConfig.nps_url_token?.trim() &&
    npsConfig.nps_url_whatsapp?.trim() &&
    npsConfig.nps_cliente_id?.trim() &&
    npsConfig.nps_secret_id?.trim() &&
    npsConfig.nps_bot_id?.trim() &&
    npsConfig.nps_template?.trim()
  );
  
  // El admin siempre puede toggle, otros solo si está configurado
  const puedeHabilitarNps = isAdmin || camposNpsConfigurados;
  
  const [loading, setLoading] = useState(true);
  const [loadingCatalogos, setLoadingCatalogos] = useState({
    categorias: false,
    lugares: false,
    items: false,
    fallas: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Usar endpoint optimizado que trae todo en una sola llamada
      const response = await configuracionGeneralAPI.getPageData();
      const data = response.data;
      
      // Actualizar estados con los datos recibidos
      setSupervisores(data.supervisores || []);
      setProyectos(data.proyectos || []);
      setSubdivisiones(data.subdivisiones || {});
      setAsignaciones(data.asignaciones || []);
      
      // Actualizar catálogos
      const catalogos = data.catalogos || {};
      setCategorias(catalogos.categorias || []);
      setLugares(catalogos.lugares || []);
      setItems(catalogos.items || []);
      setFallas(catalogos.fallas || []);
      
      // Actualizar configuración NPS
      const config = data.config_nps || {};
      setNpsConfig({
        nps_habilitado: config.nps_habilitado || false,
        nps_url_token: config.nps_url_token || '',
        nps_cliente_id: config.nps_cliente_id || '',
        nps_secret_id: config.nps_secret_id || '',
        nps_url_whatsapp: config.nps_url_whatsapp || '',
        nps_bot_id: config.nps_bot_id || '',
        nps_template: config.nps_template || '',
        templates_whatsapp: config.templates_whatsapp || [],
        aprobar_acta_inspeccion_whatsapp: config.aprobar_acta_inspeccion_whatsapp || false,
        aprobar_acta_atencion_whatsapp: config.aprobar_acta_atencion_whatsapp || false,
        aprobar_acta_diaria_whatsapp: config.aprobar_acta_diaria_whatsapp || false
      });
      
      // Actualizar configuración de notificaciones
      setNotificacionesConfig({
        notificar_contratistas_auto: config.notificar_contratistas_auto || false
      });
      
      // Actualizar configuración general
      setGeneralConfig({
        limite_reprogramaciones: config.limite_reprogramaciones ?? 3,
        primera_respuesta_autoagendamiento_activo: config.primera_respuesta_autoagendamiento_activo ?? false,
        recordatorio_autoagendamiento_activo: config.recordatorio_autoagendamiento_activo ?? false,
        limite_recordatorios_autoagendamientos: config.limite_recordatorios_autoagendamientos ?? 3,
        api_callback_url: config.api_callback_url || '',
        daily_notifications_activo: config.daily_notifications_activo ?? false,
        stand_by_check_activo: config.stand_by_check_activo ?? false
      });
    } catch (error) {
      toast.error('Error al cargar datos');
      console.error('Error loading configuracion:', error);
    } finally {
      setLoading(false);
    }
    
    // Cargar tabla de asignaciones por separado
    setLoadingAsignaciones(true);
    try {
      const tablaResp = await asignacionesAPI.getTabla();
      setTablaAsignaciones(tablaResp.data || []);
      setTablaModificada(false);
    } catch (error) {
      console.error('Error loading tabla asignaciones:', error);
    } finally {
      setLoadingAsignaciones(false);
    }
  };

  const loadCatalogos = async () => {
    try {
      const [catRes, lugRes, itemRes, falRes] = await Promise.all([
        catalogosAPI.getCategorias(),
        catalogosAPI.getLugares(),
        catalogosAPI.getItems(),
        catalogosAPI.getFallas()
      ]);
      setCategorias(catRes.data || []);
      setLugares(lugRes.data || []);
      setItems(itemRes.data || []);
      setFallas(falRes.data || []);
    } catch (error) {
      console.error('Error loading catalogos:', error);
    }
  };

  // Funciones para catálogos
  const handleExport = async (tipo) => {
    try {
      let response;
      switch (tipo) {
        case 'categorias': response = await catalogosAPI.exportCategorias(); break;
        case 'lugares': response = await catalogosAPI.exportLugares(); break;
        case 'items': response = await catalogosAPI.exportItems(); break;
        case 'fallas': response = await catalogosAPI.exportFallas(); break;
        default: return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `catalogo_${tipo}_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Archivo exportado');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleImport = async (tipo, file) => {
    try {
      let response;
      switch (tipo) {
        case 'categorias': response = await catalogosAPI.importCategorias(file); break;
        case 'lugares': response = await catalogosAPI.importLugares(file); break;
        case 'items': response = await catalogosAPI.importItems(file); break;
        case 'fallas': response = await catalogosAPI.importFallas(file); break;
        default: return;
      }
      
      const result = response.data;
      
      // Mostrar errores detallados si los hay
      if (result.errors?.length > 0) {
        const errorDetails = result.errors.map(e => `Fila ${e.fila}: ${e.error}`).join('\n');
        toast.error(`Importación detenida por errores:\n${errorDetails}`, {
          duration: 10000
        });
      }
      
      // Mostrar estadísticas
      const inserted = result.inserted || 0;
      const updated = result.updated || 0;
      const errorCount = result.errors?.length || 0;
      const statsMsg = `Insertados: ${inserted}, Actualizados: ${updated}, Errores: ${errorCount}`;
      
      if (result.success) {
        toast.success(statsMsg);
      } else {
        toast.warning(statsMsg);
      }
      
      loadCatalogos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar');
    }
  };

  // Helpers para asignaciones
  const getAsignacionesSupervisor = (supervisorId) => {
    return asignaciones.filter(a => a.supervisor_id === supervisorId);
  };

  const getProyectoNombre = (proyectoId) => {
    return proyectos.find(p => p.id === proyectoId)?.nombre || 'Desconocido';
  };

  // Exportar asignaciones a Excel
  const handleExportAsignaciones = async () => {
    try {
      const response = await asignacionesAPI.exportExcel();
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'asignaciones_supervisores.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Archivo exportado correctamente');
    } catch (error) {
      console.error('Error exportando:', error);
      toast.error('Error al exportar asignaciones');
    }
  };

  // Importar asignaciones desde Excel
  const handleImportAsignaciones = async () => {
    if (!importFile) {
      toast.error('Seleccione un archivo');
      return;
    }
    
    setImportLoading(true);
    setImportResult(null);
    
    try {
      const response = await asignacionesAPI.importExcel(importFile);
      setImportResult(response.data);
      
      if (response.data.success) {
        toast.success('Importación completada exitosamente');
        // Recargar asignaciones
        loadData();
      }
    } catch (error) {
      console.error('Error importando:', error);
      const errorMsg = error.response?.data?.detail || 'Error al importar asignaciones';
      toast.error(errorMsg);
      setImportResult({
        success: false,
        message: errorMsg,
        errores: []
      });
    } finally {
      setImportLoading(false);
    }
  };

  // Cambiar supervisor en tabla de asignaciones
  const handleTablaSupervisorChange = (index, supervisorId) => {
    const newTabla = [...tablaAsignaciones];
    const row = newTabla[index];
    const newSupervisor = supervisorId === 'ninguno' ? null : supervisorId;
    row.supervisor_id = newSupervisor;
    
    // Regla de cascada: si es una etapa, replicar a bloques hijos SIN supervisor asignado
    if (row.row_type === 'etapa' && row.etapa_id && newSupervisor) {
      for (let i = index + 1; i < newTabla.length; i++) {
        const child = newTabla[i];
        if (child.proyecto_id === row.proyecto_id && child.etapa_id === row.etapa_id && child.row_type === 'bloque') {
          if (!child.supervisor_id) {
            child.supervisor_id = newSupervisor;
          }
        } else if (child.proyecto_id !== row.proyecto_id || child.etapa_id !== row.etapa_id) {
          break;
        }
      }
    }
    
    setTablaAsignaciones(newTabla);
    setTablaModificada(true);
  };

  // Guardar tabla de asignaciones
  const handleGuardarTabla = async () => {
    setSavingTabla(true);
    try {
      const filas = tablaAsignaciones.map(row => ({
        proyecto_id: row.proyecto_id,
        proyecto_nombre: row.proyecto_nombre,
        etapa_id: row.etapa_id,
        etapa_nombre: row.etapa_nombre,
        bloque_id: row.bloque_id,
        bloque_nombre: row.bloque_nombre,
        supervisor_id: row.supervisor_id,
        url_booking: row.url_booking || null,
        row_type: row.row_type
      }));
      await asignacionesAPI.guardarTabla(filas);
      toast.success('Asignaciones guardadas correctamente');
      setTablaModificada(false);
      // Reload to sync
      const tablaResp = await asignacionesAPI.getTabla();
      setTablaAsignaciones(tablaResp.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar asignaciones');
    } finally {
      setSavingTabla(false);
    }
  };

  // Cambiar url_booking en tabla de asignaciones con cascada
  const handleTablaUrlBookingChange = (index, value) => {
    const newTabla = [...tablaAsignaciones];
    const row = newTabla[index];
    row.url_booking = value;
    
    // Regla de cascada: si es una etapa, copiar a bloques hijos SIN url_booking
    if (row.row_type === 'etapa' && row.etapa_id && value) {
      for (let i = index + 1; i < newTabla.length; i++) {
        const child = newTabla[i];
        if (child.proyecto_id === row.proyecto_id && child.etapa_id === row.etapa_id && child.row_type === 'bloque') {
          if (!child.url_booking) {
            child.url_booking = value;
          }
        } else if (child.proyecto_id !== row.proyecto_id || child.etapa_id !== row.etapa_id) {
          break;
        }
      }
    }
    
    setTablaAsignaciones(newTabla);
    setTablaModificada(true);
  };

  // Cerrar modal de importación
  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
  };

  const getSubdivisionNombre = (proyectoId, subdivisionId) => {
    const subs = subdivisiones[proyectoId] || [];
    return subs.find(s => s.id === subdivisionId)?.nombre || '';
  };

  const isProyectoAsignado = (proyectoId, subdivisionId = null) => {
    return asignaciones.some(a => 
      a.proyecto_id === proyectoId && 
      (subdivisionId ? a.subdivision_id === subdivisionId : !a.subdivision_id)
    );
  };

  const handleAsignar = async (supervisorId, proyectoId, subdivisionId = null) => {
    try {
      await asignacionesAPI.create({
        supervisor_id: supervisorId,
        proyecto_id: proyectoId,
        subdivision_id: subdivisionId
      });
      toast.success('Asignación creada');
      const res = await asignacionesAPI.getAll();
      setAsignaciones(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar');
    }
  };

  const handleAsignarTodo = async (supervisorId, proyectoId) => {
    const subs = subdivisiones[proyectoId] || [];
    if (subs.length === 0) {
      toast.error('No hay subdivisiones para asignar');
      return;
    }
    
    let asignadas = 0;
    let errores = 0;
    
    for (const sub of subs) {
      // Verificar si ya está asignada
      if (!isProyectoAsignado(proyectoId, sub.id)) {
        try {
          await asignacionesAPI.create({
            supervisor_id: supervisorId,
            proyecto_id: proyectoId,
            subdivision_id: sub.id
          });
          asignadas++;
        } catch (error) {
          errores++;
        }
      }
    }
    
    if (asignadas > 0) {
      toast.success(`${asignadas} subdivisiones asignadas`);
    }
    if (errores > 0) {
      toast.warning(`${errores} subdivisiones no pudieron ser asignadas`);
    }
    
    const res = await asignacionesAPI.getAll();
    setAsignaciones(res.data || []);
  };

  const handleDesasignar = async (asignacionId) => {
    if (!window.confirm('¿Está seguro de eliminar esta asignación?')) return;
    try {
      await asignacionesAPI.delete(asignacionId);
      toast.success('Asignación eliminada');
      const res = await asignacionesAPI.getAll();
      setAsignaciones(res.data || []);
    } catch (error) {
      toast.error('Error al eliminar asignación');
    }
  };

  // Guardar configuración NPS
  const handleSaveNpsConfig = async () => {
    setSavingNps(true);
    try {
      await configuracionGeneralAPI.update(npsConfig);
      toast.success('Configuración NPS guardada correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración NPS');
    } finally {
      setSavingNps(false);
    }
  };

  // Guardar configuración de notificaciones a contratistas
  const handleSaveNotificacionesConfig = async () => {
    setSavingNotificaciones(true);
    try {
      await configuracionGeneralAPI.update(notificacionesConfig);
      toast.success('Configuración de notificaciones guardada correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSavingNotificaciones(false);
    }
  };

  // Guardar configuración general
  const handleSaveGeneralConfig = async () => {
    const limite = generalConfig.limite_reprogramaciones;
    if (!limite || limite < 1) {
      toast.error('El límite de reprogramaciones debe ser al menos 1');
      return;
    }
    if (generalConfig.recordatorio_autoagendamiento_activo) {
      const limiteAuto = generalConfig.limite_recordatorios_autoagendamientos;
      if (!limiteAuto || limiteAuto < 1) {
        toast.error('El límite de recordatorios de autoagendamiento debe ser al menos 1');
        return;
      }
    }
    setSavingGeneral(true);
    try {
      await configuracionGeneralAPI.update({
        limite_reprogramaciones: parseInt(limite),
        primera_respuesta_autoagendamiento_activo: generalConfig.primera_respuesta_autoagendamiento_activo,
        recordatorio_autoagendamiento_activo: generalConfig.recordatorio_autoagendamiento_activo,
        limite_recordatorios_autoagendamientos: parseInt(generalConfig.limite_recordatorios_autoagendamientos),
        api_callback_url: generalConfig.api_callback_url || null
      });
      toast.success('Configuración general guardada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingGeneral(false);
    }
  };

  // Guardar configuración Notificaciones Internas
  const handleSaveNotifInternas = async () => {
    setSavingNotifInternas(true);
    try {
      await configuracionGeneralAPI.update({
        daily_notifications_activo: generalConfig.daily_notifications_activo,
        stand_by_check_activo: generalConfig.stand_by_check_activo
      });
      toast.success('Notificaciones internas actualizadas');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingNotifInternas(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9d8272]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="configuracion-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Configuración
        </h1>
        <p className="text-sm lg:text-lg text-[#5A5A5A] mt-1">
          Asigna supervisores a proyectos y gestiona los catálogos del sistema
        </p>
      </div>

      {/* Sección General */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm">
        <CardHeader className="px-3 sm:px-6">
          <div className="flex items-center gap-2">
            <Settings size={18} className="text-[#9d8272] flex-shrink-0" />
            <CardTitle className="text-sm sm:text-lg font-semibold">General</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
            <div>
              <Label className="text-xs sm:text-sm font-medium text-[#5A5A5A]">
                Límite de Reprogramaciones <span className="text-red-500">*</span>
              </Label>
              <p className="text-[10px] text-[#888888] mb-1">
                Cantidad máxima de veces que se puede reprogramar la fecha de inspección
              </p>
              <Input
                type="number"
                min={1}
                value={generalConfig.limite_reprogramaciones}
                onChange={(e) => setGeneralConfig({ ...generalConfig, limite_reprogramaciones: e.target.value })}
                className="border-[#c5b8a2] text-sm h-9 w-32"
                data-testid="limite-reprogramaciones-input"
              />
            </div>
          </div>

          {/* Primera Respuesta de Autoagendamiento */}
          <div className="mt-5 pt-4 border-t border-[#E8E8E7]">
            <div className="flex items-center gap-3 mb-3">
              <button
                type="button"
                onClick={() => setGeneralConfig(prev => ({ ...prev, primera_respuesta_autoagendamiento_activo: !prev.primera_respuesta_autoagendamiento_activo }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  generalConfig.primera_respuesta_autoagendamiento_activo ? 'bg-green-500' : 'bg-gray-300'
                }`}
                data-testid="primera-respuesta-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  generalConfig.primera_respuesta_autoagendamiento_activo ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
              <div className="flex-1">
                <Label className="text-xs sm:text-sm font-medium">Enviar primera respuesta para autoagendamiento</Label>
                <p className="text-[10px] text-[#888888]">
                  {generalConfig.primera_respuesta_autoagendamiento_activo 
                    ? 'Se enviará automáticamente un recordatorio de autoagendamiento por WhatsApp al crear un nuevo requerimiento' 
                    : 'La primera respuesta de autoagendamiento está desactivada'}
                </p>
              </div>
            </div>
          </div>

          {/* URL de Callback para Trigger (visible solo para Admin) */}
          {isAdmin && (
            <div className="mt-3">
              <Label className={`text-xs sm:text-sm font-medium ${!generalConfig.primera_respuesta_autoagendamiento_activo ? 'text-gray-400' : 'text-[#5A5A5A]'}`}>
                URL Callback API
              </Label>
              <p className="text-[10px] text-[#888888] mb-1">
                URL base del backend para triggers de base de datos (ej: https://mi-app.com). Necesario para primera respuesta vía REST API.
              </p>
              <Input
                type="url"
                value={generalConfig.api_callback_url}
                onChange={(e) => setGeneralConfig({ ...generalConfig, api_callback_url: e.target.value })}
                placeholder="https://mi-app.com"
                className={`border-[#c5b8a2] text-sm h-9 max-w-md ${!generalConfig.primera_respuesta_autoagendamiento_activo ? 'bg-gray-100 opacity-60' : ''}`}
                disabled={!generalConfig.primera_respuesta_autoagendamiento_activo}
                data-testid="api-callback-url-input"
              />
            </div>
          )}

          {/* Recordatorio de Autoagendamiento */}
          <div className="mt-5 pt-4 border-t border-[#E8E8E7]">
            <div className="flex items-center gap-3 mb-3">
              <button
                type="button"
                onClick={() => setGeneralConfig(prev => ({ ...prev, recordatorio_autoagendamiento_activo: !prev.recordatorio_autoagendamiento_activo }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  generalConfig.recordatorio_autoagendamiento_activo ? 'bg-green-500' : 'bg-gray-300'
                }`}
                data-testid="autoagendamiento-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  generalConfig.recordatorio_autoagendamiento_activo ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
              <div className="flex-1">
                <Label className="text-xs sm:text-sm font-medium">Activar recordatorio de autoagendamiento</Label>
                <p className="text-[10px] text-[#888888]">
                  {generalConfig.recordatorio_autoagendamiento_activo 
                    ? 'Se enviarán recordatorios diarios por WhatsApp a solicitantes sin inspección programada' 
                    : 'Los recordatorios de autoagendamiento están desactivados'}
                </p>
              </div>
            </div>
            
            <div className={`${!generalConfig.recordatorio_autoagendamiento_activo ? 'opacity-50 pointer-events-none' : ''}`}>
              <Label className="text-xs sm:text-sm font-medium text-[#5A5A5A]">
                Límite de recordatorios de autoagendamiento
              </Label>
              <p className="text-[10px] text-[#888888] mb-1">
                Cantidad máxima de días (y envíos) posteriores a la creación del requerimiento para recordar al solicitante
              </p>
              <Input
                type="number"
                min={1}
                value={generalConfig.limite_recordatorios_autoagendamientos}
                onChange={(e) => setGeneralConfig({ ...generalConfig, limite_recordatorios_autoagendamientos: e.target.value })}
                className="border-[#c5b8a2] text-sm h-9 w-32"
                disabled={!generalConfig.recordatorio_autoagendamiento_activo}
                data-testid="limite-autoagendamiento-input"
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button
              onClick={handleSaveGeneralConfig}
              disabled={savingGeneral}
              className="bg-[#9d8272] hover:bg-[#8a7060] text-white h-9 text-xs sm:text-sm"
              data-testid="save-general-config-btn"
            >
              {savingGeneral ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
              Guardar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sección 1: Asignación de Supervisores */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm">
        <CardHeader className="px-3 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-[#9d8272] flex-shrink-0" />
              <CardTitle className="text-sm sm:text-lg font-semibold">Asignación de Supervisores</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportAsignaciones}
                className="text-[#9d8272] border-[#9d8272] hover:bg-[#9d8272]/10"
                data-testid="export-asignaciones-btn"
              >
                <Download size={16} className="mr-1" />
                <span className="hidden sm:inline">Exportar</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowImportModal(true)}
                className="text-[#9d8272] border-[#9d8272] hover:bg-[#9d8272]/10"
                data-testid="import-asignaciones-btn"
              >
                <Upload size={16} className="mr-1" />
                <span className="hidden sm:inline">Importar</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          {loadingAsignaciones ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#9d8272]" />
              <span className="ml-2 text-sm text-[#888888]">Cargando asignaciones...</span>
            </div>
          ) : (
          <>
          {/* Vista Mobile: tarjetas */}
          <div className="md:hidden max-h-[420px] overflow-y-auto space-y-1">
            {tablaAsignaciones.length === 0 ? (
              <p className="text-center py-8 text-[#888888] text-sm">No hay proyectos configurados</p>
            ) : (
              tablaAsignaciones.map((row, index) => {
                const isFirstOfProject = index === 0 || tablaAsignaciones[index - 1].proyecto_id !== row.proyecto_id;
                return (
                  <React.Fragment key={`m-${row.proyecto_id}-${row.etapa_id || ''}-${row.bloque_id || ''}`}>
                    {isFirstOfProject && index > 0 && <div className="border-t border-[#c5b8a2] my-2" />}
                    <div className={`p-2.5 rounded-lg border ${isFirstOfProject ? 'border-[#c5b8a2] bg-[#FAFAFA]' : 'border-[#E8E8E7] bg-white ml-3'}`} data-testid={`asignacion-card-${index}`}>
                      <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                        {isFirstOfProject && <Building2 size={12} className="text-[#9d8272] flex-shrink-0" />}
                        <span className={`text-xs ${isFirstOfProject ? 'font-semibold text-[#2D2D2D]' : 'text-[#555]'}`}>
                          {isFirstOfProject ? row.proyecto_nombre : ''}
                          {row.etapa_nombre ? `${isFirstOfProject ? ' › ' : ''}${row.etapa_nombre}` : ''}
                          {row.bloque_nombre ? ` › ${row.bloque_nombre}` : ''}
                          {!row.etapa_nombre && !row.bloque_nombre && !isFirstOfProject ? row.proyecto_nombre : ''}
                        </span>
                      </div>
                      <Select
                        value={row.supervisor_id || 'ninguno'}
                        onValueChange={(val) => handleTablaSupervisorChange(index, val)}
                      >
                        <SelectTrigger className="h-8 text-xs border-[#c5b8a2] w-full" data-testid={`supervisor-select-m-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ninguno"><span className="text-[#888888]">Ninguno</span></SelectItem>
                          {supervisores.map(sup => (
                            <SelectItem key={sup.id} value={sup.id}>{sup.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={row.url_booking || ''}
                        onChange={(e) => handleTablaUrlBookingChange(index, e.target.value)}
                        placeholder="URL Booking: https://..."
                        className="h-8 text-xs border-[#c5b8a2] mt-1.5"
                        data-testid={`url-booking-m-${index}`}
                      />
                    </div>
                  </React.Fragment>
                );
              })
            )}
          </div>

          {/* Vista Desktop: tabla */}
          <div className="hidden md:block overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm border-collapse" data-testid="tabla-asignaciones">
              <thead className="bg-[#F5F5F5] sticky top-0 z-10">
                <tr>
                  <th className="text-left p-1.5 sm:p-2 font-medium text-xs sm:text-sm">Proyecto</th>
                  <th className="text-left p-1.5 sm:p-2 font-medium text-xs sm:text-sm">Etapa</th>
                  <th className="text-left p-1.5 sm:p-2 font-medium text-xs sm:text-sm">Bloque</th>
                  <th className="text-left p-1.5 sm:p-2 font-medium text-xs sm:text-sm min-w-[200px]">Supervisor Asignado</th>
                  <th className="text-left p-1.5 sm:p-2 font-medium text-xs sm:text-sm min-w-[200px]">URL Booking</th>
                </tr>
              </thead>
              <tbody>
                {tablaAsignaciones.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-[#888888] text-sm">
                      No hay proyectos configurados
                    </td>
                  </tr>
                ) : (
                  tablaAsignaciones.map((row, index) => {
                    const isFirstOfProject = index === 0 || tablaAsignaciones[index - 1].proyecto_id !== row.proyecto_id;
                    const isFirstOfEtapa = index === 0 || tablaAsignaciones[index - 1].etapa_id !== row.etapa_id || tablaAsignaciones[index - 1].proyecto_id !== row.proyecto_id;
                    const projectRowCount = tablaAsignaciones.filter(r => r.proyecto_id === row.proyecto_id).length;
                    const etapaRowCount = tablaAsignaciones.filter(r => r.proyecto_id === row.proyecto_id && r.etapa_id === row.etapa_id).length;
                    
                    return (
                      <tr 
                        key={`${row.proyecto_id}-${row.etapa_id || ''}-${row.bloque_id || ''}`}
                        className={`border-b border-[#E8E8E7] hover:bg-[#FAFAFA] ${row.row_type === 'bloque' ? 'bg-white' : 'bg-[#FAFAFA]'}`}
                        data-testid={`asignacion-row-${index}`}
                      >
                        {isFirstOfProject && (
                          <td className="px-3 py-2 font-medium text-xs text-[#2D2D2D] border-r border-[#E8E8E7] align-top" rowSpan={projectRowCount}>
                            <div className="flex items-center gap-1.5">
                              <Building2 size={14} className="text-[#9d8272] flex-shrink-0" />
                              {row.proyecto_nombre}
                            </div>
                          </td>
                        )}
                        {isFirstOfEtapa && (
                          <td className="px-3 py-2 text-xs text-[#555] border-r border-[#E8E8E7] align-top" rowSpan={etapaRowCount}>
                            {row.etapa_nombre || '-'}
                          </td>
                        )}
                        <td className="px-3 py-2 text-xs text-[#555] border-r border-[#E8E8E7]">
                          {row.bloque_nombre || '-'}
                        </td>
                        <td className="px-3 py-1.5">
                          <Select
                            value={row.supervisor_id || 'ninguno'}
                            onValueChange={(val) => handleTablaSupervisorChange(index, val)}
                          >
                            <SelectTrigger className="h-8 text-xs border-[#c5b8a2]" data-testid={`supervisor-select-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ninguno">
                                <span className="text-[#888888]">Ninguno</span>
                              </SelectItem>
                              {supervisores.map(sup => (
                                <SelectItem key={sup.id} value={sup.id}>
                                  {sup.nombre}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-1.5">
                          <Input
                            value={row.url_booking || ''}
                            onChange={(e) => handleTablaUrlBookingChange(index, e.target.value)}
                            placeholder="https://..."
                            className="h-8 text-xs border-[#c5b8a2]"
                            data-testid={`url-booking-${index}`}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {tablaAsignaciones.length > 0 && (
            <div className="flex justify-end mt-4">
              <Button
                onClick={handleGuardarTabla}
                disabled={!tablaModificada || savingTabla}
                className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
                data-testid="guardar-asignaciones-btn"
              >
                {savingTabla ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Guardar Cambios
                  </>
                )}
              </Button>
            </div>
          )}
          </>
          )}
        </CardContent>
      </Card>

      {/* Sección 2: Catálogos del Sistema */}
      <div>
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <Settings size={18} className="text-[#9d8272]" />
          <h2 className="text-sm sm:text-lg font-semibold text-[#2D2D2D]">Catálogos del Sistema</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          <CatalogoManager
            title="Categorías"
            icon={Home}
            items={categorias}
            loading={loadingCatalogos.categorias}
            onAdd={async (data) => {
              try {
                await catalogosAPI.createCategoria(data);
                toast.success('Categoría creada');
                loadCatalogos();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al crear');
              }
            }}
            onEdit={async (id, data) => {
              try {
                await catalogosAPI.updateCategoria(id, data);
                toast.success('Categoría actualizada');
                loadCatalogos();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al actualizar');
              }
            }}
            onDelete={async (id) => {
              if (!window.confirm('¿Eliminar esta categoría?')) return;
              try {
                await catalogosAPI.deleteCategoria(id);
                toast.success('Categoría eliminada');
                loadCatalogos();
              } catch (e) {
                toast.error('Error al eliminar');
              }
            }}
            onDeleteAll={async () => {
              try {
                const result = await catalogosAPI.deleteAllCategorias();
                toast.success(result.data?.message || `Se eliminaron ${result.data?.deleted || 0} categorías`);
                loadCatalogos();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al eliminar todo');
              }
            }}
            onToggleActivo={async (id, activo) => {
              try {
                await catalogosAPI.updateCategoria(id, { activo });
                toast.success(`Categoría ${activo ? 'activada' : 'desactivada'}`);
                loadCatalogos();
              } catch (e) {
                toast.error('Error al cambiar estado');
              }
            }}
            onExport={() => handleExport('categorias')}
            onImport={(file) => handleImport('categorias', file)}
          />

          <CatalogoManager
            title="Lugares"
            icon={MapPin}
            items={lugares}
            loading={loadingCatalogos.lugares}
            onAdd={async (data) => {
              try {
                await catalogosAPI.createLugar(data);
                toast.success('Lugar creado');
                loadCatalogos();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al crear');
              }
            }}
            onEdit={async (id, data) => {
              try {
                await catalogosAPI.updateLugar(id, data);
                toast.success('Lugar actualizado');
                loadCatalogos();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al actualizar');
              }
            }}
            onDelete={async (id) => {
              if (!window.confirm('¿Eliminar este lugar?')) return;
              try {
                await catalogosAPI.deleteLugar(id);
                toast.success('Lugar eliminado');
                loadCatalogos();
              } catch (e) {
                toast.error('Error al eliminar');
              }
            }}
            onDeleteAll={async () => {
              try {
                const result = await catalogosAPI.deleteAllLugares();
                toast.success(result.data?.message || `Se eliminaron ${result.data?.deleted || 0} lugares`);
                loadCatalogos();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al eliminar todo');
              }
            }}
            onToggleActivo={async (id, activo) => {
              try {
                await catalogosAPI.updateLugar(id, { activo });
                toast.success(`Lugar ${activo ? 'activado' : 'desactivado'}`);
                loadCatalogos();
              } catch (e) {
                toast.error('Error al cambiar estado');
              }
            }}
            onExport={() => handleExport('lugares')}
            onImport={(file) => handleImport('lugares', file)}
          />

          <CatalogoManager
            title="Items"
            icon={Wrench}
            items={items}
            loading={loadingCatalogos.items}
            parentField="categoria_nombre"
            parentLabel="Categoría"
            parentOptions={categorias}
            onAdd={async (data) => {
              try {
                await catalogosAPI.createItem(data);
                toast.success('Item creado');
                loadCatalogos();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al crear');
              }
            }}
            onEdit={async (id, data) => {
              try {
                await catalogosAPI.updateItem(id, data);
                toast.success('Item actualizado');
                loadCatalogos();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al actualizar');
              }
            }}
            onDelete={async (id) => {
              if (!window.confirm('¿Eliminar este item?')) return;
              try {
                await catalogosAPI.deleteItem(id);
                toast.success('Item eliminado');
                loadCatalogos();
              } catch (e) {
                toast.error('Error al eliminar');
              }
            }}
            onDeleteAll={async () => {
              try {
                const result = await catalogosAPI.deleteAllItems();
                toast.success(result.data?.message || `Se eliminaron ${result.data?.deleted || 0} items`);
                loadCatalogos();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al eliminar todo');
              }
            }}
            onToggleActivo={async (id, activo) => {
              try {
                await catalogosAPI.updateItem(id, { activo });
                toast.success(`Item ${activo ? 'activado' : 'desactivado'}`);
                loadCatalogos();
              } catch (e) {
                toast.error('Error al cambiar estado');
              }
            }}
            onExport={() => handleExport('items')}
            onImport={(file) => handleImport('items', file)}
          />

          <CatalogoManager
            title="Tipos de Falla"
            icon={AlertTriangle}
            items={fallas}
            loading={loadingCatalogos.fallas}
            parentField="categoria_nombre"
            parentLabel="Categoría"
            parentOptions={categorias}
            onAdd={async (data) => {
              try {
                await catalogosAPI.createFalla(data);
                toast.success('Tipo de falla creado');
                loadCatalogos();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al crear');
              }
            }}
            onEdit={async (id, data) => {
              try {
                await catalogosAPI.updateFalla(id, data);
                toast.success('Tipo de falla actualizado');
                loadCatalogos();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al actualizar');
              }
            }}
            onDelete={async (id) => {
              if (!window.confirm('¿Eliminar este tipo de falla?')) return;
              try {
                await catalogosAPI.deleteFalla(id);
                toast.success('Tipo de falla eliminado');
                loadCatalogos();
              } catch (e) {
                toast.error('Error al eliminar');
              }
            }}
            onDeleteAll={async () => {
              try {
                const result = await catalogosAPI.deleteAllFallas();
                toast.success(result.data?.message || `Se eliminaron ${result.data?.deleted || 0} tipos de falla`);
                loadCatalogos();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al eliminar todo');
              }
            }}
            onToggleActivo={async (id, activo) => {
              try {
                await catalogosAPI.updateFalla(id, { activo });
                toast.success(`Tipo de falla ${activo ? 'activado' : 'desactivado'}`);
                loadCatalogos();
              } catch (e) {
                toast.error('Error al cambiar estado');
              }
            }}
            onExport={() => handleExport('fallas')}
            onImport={(file) => handleImport('fallas', file)}
          />
        </div>
      </div>

      {/* Sección 3: Configuración NPS por WhatsApp */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm">
        <CardHeader className="px-3 sm:px-6">
          <div className="flex items-center gap-2">
            <MessageCircle size={18} className="text-[#9d8272] flex-shrink-0" />
            <CardTitle className="text-sm sm:text-lg font-semibold">Configuración Comunicación por WhatsApp</CardTitle>
            <Badge 
              className={`ml-auto ${npsConfig.nps_habilitado ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
            >
              {npsConfig.nps_habilitado ? 'Habilitado' : 'Deshabilitado'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          <p className="text-xs sm:text-sm text-[#888888] mb-4">
            {isAdmin 
              ? 'Configura la integración con SendPulse para comunicaciones por WhatsApp: encuestas NPS, recordatorios de autoagendamiento y notificaciones de actas.'
              : 'Habilita o deshabilita el envío automático de comunicaciones por WhatsApp.'
            }
          </p>
          
          <div className="space-y-4">
            {/* Switch de habilitación */}
            <div className={`flex items-center gap-3 p-3 rounded-lg border ${
              !puedeHabilitarNps 
                ? 'bg-amber-50 border-amber-200' 
                : 'bg-[#FAFAFA] border-[#E8E8E7]'
            }`}>
              <button
                type="button"
                onClick={() => {
                  if (puedeHabilitarNps) {
                    setNpsConfig(prev => ({ ...prev, nps_habilitado: !prev.nps_habilitado }));
                  }
                }}
                disabled={!puedeHabilitarNps}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  !puedeHabilitarNps 
                    ? 'bg-gray-200 cursor-not-allowed opacity-50' 
                    : npsConfig.nps_habilitado 
                      ? 'bg-green-500' 
                      : 'bg-gray-300'
                }`}
                data-testid="nps-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    npsConfig.nps_habilitado ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <div className="flex-1">
                <Label className="text-sm font-medium">Habilitar envío de NPS</Label>
                <p className="text-xs text-[#888888]">
                  {npsConfig.nps_habilitado 
                    ? 'El sistema enviará encuestas NPS automáticamente' 
                    : 'Las encuestas NPS están desactivadas'}
                </p>
              </div>
            </div>

            {/* Switches de Aprobación de Actas por WhatsApp */}
            <div className="space-y-2 pt-2">
              <p className="text-xs text-[#888888] font-medium">Aprobación de Actas por WhatsApp</p>
              
              {/* Switch: Actas de Inspección */}
              <div className="flex items-center gap-3 p-2 rounded-lg bg-[#FAFAFA] border border-[#E8E8E7]">
                <button
                  type="button"
                  onClick={() => setNpsConfig(prev => ({ ...prev, aprobar_acta_inspeccion_whatsapp: !prev.aprobar_acta_inspeccion_whatsapp }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    npsConfig.aprobar_acta_inspeccion_whatsapp ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  data-testid="aprobar-acta-inspeccion-whatsapp-toggle"
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    npsConfig.aprobar_acta_inspeccion_whatsapp ? 'translate-x-4' : 'translate-x-0.5'
                  }`} />
                </button>
                <Label className="text-xs sm:text-sm">Aprobar actas de inspección por WhatsApp</Label>
              </div>

              {/* Switch: Actas de Atención */}
              <div className="flex items-center gap-3 p-2 rounded-lg bg-[#FAFAFA] border border-[#E8E8E7]">
                <button
                  type="button"
                  onClick={() => setNpsConfig(prev => ({ ...prev, aprobar_acta_atencion_whatsapp: !prev.aprobar_acta_atencion_whatsapp }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    npsConfig.aprobar_acta_atencion_whatsapp ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  data-testid="aprobar-acta-atencion-whatsapp-toggle"
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    npsConfig.aprobar_acta_atencion_whatsapp ? 'translate-x-4' : 'translate-x-0.5'
                  }`} />
                </button>
                <Label className="text-xs sm:text-sm">Aprobar actas de atención por WhatsApp</Label>
              </div>

              {/* Switch: Actas Diarias */}
              <div className="flex items-center gap-3 p-2 rounded-lg bg-[#FAFAFA] border border-[#E8E8E7]">
                <button
                  type="button"
                  onClick={() => setNpsConfig(prev => ({ ...prev, aprobar_acta_diaria_whatsapp: !prev.aprobar_acta_diaria_whatsapp }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    npsConfig.aprobar_acta_diaria_whatsapp ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  data-testid="aprobar-acta-diaria-whatsapp-toggle"
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                    npsConfig.aprobar_acta_diaria_whatsapp ? 'translate-x-4' : 'translate-x-0.5'
                  }`} />
                </button>
                <Label className="text-xs sm:text-sm">Aprobar actas diarias por WhatsApp</Label>
              </div>
            </div>

            {/* Advertencia para Jefe de Postventa si campos no configurados */}
            {!isAdmin && !camposNpsConfigurados && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    El envío de NPS no puede habilitarse
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Los parámetros de integración con SendPulse no han sido configurados. 
                    Contacte al Administrador para completar la configuración.
                  </p>
                </div>
              </div>
            )}

            {/* Mensaje de confirmación para Jefe de Postventa si campos SÍ configurados */}
            {!isAdmin && camposNpsConfigurados && (
              <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Integración NPS configurada correctamente
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Puede habilitar o deshabilitar el envío automático de encuestas NPS.
                  </p>
                </div>
              </div>
            )}

            {/* Campos de configuración - Solo visible para Administrador */}
            {isAdmin && (
              <>
                <div className="flex items-center gap-2 pt-2">
                  <Info size={14} className="text-[#9d8272]" />
                  <span className="text-xs text-[#888888]">
                    Los siguientes campos solo son visibles para Administradores
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs sm:text-sm">URL Token (OAuth)</Label>
                    <Input
                      value={npsConfig.nps_url_token}
                      onChange={(e) => setNpsConfig(prev => ({ ...prev, nps_url_token: e.target.value }))}
                      placeholder="https://api.sendpulse.com/oauth/access_token"
                      className="border-[#c5b8a2] text-sm"
                      data-testid="nps-url-token"
                    />
                  </div>

                  <div>
                    <Label className="text-xs sm:text-sm">URL WhatsApp API</Label>
                    <Input
                      value={npsConfig.nps_url_whatsapp}
                      onChange={(e) => setNpsConfig(prev => ({ ...prev, nps_url_whatsapp: e.target.value }))}
                      placeholder="https://api.sendpulse.com/whatsapp/contacts/sendTemplateByPhone"
                      className="border-[#c5b8a2] text-sm"
                      data-testid="nps-url-whatsapp"
                    />
                  </div>

                  <div>
                    <Label className="text-xs sm:text-sm">Cliente ID</Label>
                    <div className="relative">
                      <Input
                        type={showSecrets ? 'text' : 'password'}
                        value={npsConfig.nps_cliente_id}
                        onChange={(e) => setNpsConfig(prev => ({ ...prev, nps_cliente_id: e.target.value }))}
                        placeholder="ID de cliente SendPulse"
                        className="border-[#c5b8a2] text-sm pr-10"
                        data-testid="nps-cliente-id"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecrets(!showSecrets)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#888888] hover:text-[#666666]"
                      >
                        {showSecrets ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs sm:text-sm">Secret ID</Label>
                    <Input
                      type={showSecrets ? 'text' : 'password'}
                      value={npsConfig.nps_secret_id}
                      onChange={(e) => setNpsConfig(prev => ({ ...prev, nps_secret_id: e.target.value }))}
                      placeholder="Secret de cliente SendPulse"
                      className="border-[#c5b8a2] text-sm"
                      data-testid="nps-secret-id"
                    />
                  </div>

                  <div>
                    <Label className="text-xs sm:text-sm">Bot ID</Label>
                    <Input
                      value={npsConfig.nps_bot_id}
                      onChange={(e) => setNpsConfig(prev => ({ ...prev, nps_bot_id: e.target.value }))}
                      placeholder="ID del bot de WhatsApp"
                      className="border-[#c5b8a2] text-sm"
                      data-testid="nps-bot-id"
                    />
                  </div>

                  <div>
                    <Label className="text-xs sm:text-sm">Nombre del Template NPS</Label>
                    <Input
                      value={npsConfig.nps_template}
                      onChange={(e) => setNpsConfig(prev => ({ ...prev, nps_template: e.target.value }))}
                      placeholder="encuesta_postventa_v2"
                      className="border-[#c5b8a2] text-sm"
                      data-testid="nps-template"
                    />
                  </div>
                </div>

                {/* Templates WhatsApp */}
                <div className="mt-4 pt-4 border-t border-[#E8E8E7]">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs sm:text-sm font-medium">Templates WhatsApp</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setNpsConfig(prev => ({
                        ...prev,
                        templates_whatsapp: [...(prev.templates_whatsapp || []), { nombre: '', valor: '' }]
                      }))}
                      className="text-xs h-7 px-2 text-[#9d8272] border-[#9d8272] hover:bg-[#9d8272]/10"
                      data-testid="add-template-btn"
                    >
                      <Plus size={12} className="mr-1" /> Agregar
                    </Button>
                  </div>
                  <p className="text-[10px] text-[#888888] mb-2">
                    Registro de plantillas de SendPulse utilizadas para comunicaciones por WhatsApp
                  </p>
                  {(npsConfig.templates_whatsapp || []).length === 0 ? (
                    <p className="text-xs text-[#888888] text-center py-3 bg-[#FAFAFA] rounded-lg border border-[#E8E8E7]">
                      No hay templates configurados
                    </p>
                  ) : (
                    <div className="border border-[#E8E8E7] rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-[#F5F5F5]">
                          <tr>
                            <th className="text-left p-2 font-medium text-xs">Nombre</th>
                            <th className="text-left p-2 font-medium text-xs">Valor (Template SendPulse)</th>
                            <th className="w-10 p-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(npsConfig.templates_whatsapp || []).map((tpl, idx) => (
                            <tr key={idx} className="border-t border-[#E8E8E7]">
                              <td className="p-1.5">
                                <Input
                                  value={tpl.nombre}
                                  onChange={(e) => {
                                    const updated = [...npsConfig.templates_whatsapp];
                                    updated[idx] = { ...updated[idx], nombre: e.target.value };
                                    setNpsConfig(prev => ({ ...prev, templates_whatsapp: updated }));
                                  }}
                                  placeholder="nombre_interno"
                                  className="border-[#c5b8a2] text-xs h-8"
                                  data-testid={`template-nombre-${idx}`}
                                />
                              </td>
                              <td className="p-1.5">
                                <Input
                                  value={tpl.valor}
                                  onChange={(e) => {
                                    const updated = [...npsConfig.templates_whatsapp];
                                    updated[idx] = { ...updated[idx], valor: e.target.value };
                                    setNpsConfig(prev => ({ ...prev, templates_whatsapp: updated }));
                                  }}
                                  placeholder="template_sendpulse_v1"
                                  className="border-[#c5b8a2] text-xs h-8"
                                  data-testid={`template-valor-${idx}`}
                                />
                              </td>
                              <td className="p-1.5 text-center">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = npsConfig.templates_whatsapp.filter((_, i) => i !== idx);
                                    setNpsConfig(prev => ({ ...prev, templates_whatsapp: updated }));
                                  }}
                                  className="text-red-400 hover:text-red-600 p-1"
                                  data-testid={`template-delete-${idx}`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Botón de guardar - visible si es admin o si puede toggle NPS */}
            {(isAdmin || puedeHabilitarNps) && (
              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSaveNpsConfig}
                  disabled={savingNps}
                  className="bg-[#9d8272] hover:bg-[#8a7162] text-white"
                  data-testid="save-nps-config"
                >
                  {savingNps ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save size={14} className="mr-2" />
                      Guardar Configuración WhatsApp
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sección: Notificaciones Internas (Scheduler) */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm" data-testid="seccion-notif-internas">
        <CardHeader className="px-3 sm:px-6">
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-[#4E7A79] flex-shrink-0" />
            <CardTitle className="text-sm sm:text-lg font-semibold">Notificaciones Internas</CardTitle>
          </div>
          <p className="text-xs text-gray-500 mt-1">Tareas programadas del sistema que envían recordatorios y ejecutan verificaciones automáticas.</p>
        </CardHeader>
        <CardContent className="px-3 sm:px-6 space-y-4">
          {/* Job 1: daily_notifications */}
          <div className="border border-[#E8E8E7] rounded-lg p-4" data-testid="job-daily-notifications">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-[#2D2D2D]">Recordatorios de inspección y atención</h4>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Envía email y notificación (campana) al supervisor cuando hay inspecciones o trabajos de atención programados para el día siguiente.
                </p>
                <div className="flex flex-wrap gap-3 mt-2">
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#888] bg-[#F5F5F5] rounded px-2 py-0.5">
                    <span className="font-medium text-[#5A5A5A]">Job ID:</span> daily_notifications
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#888] bg-[#F5F5F5] rounded px-2 py-0.5">
                    <Clock size={10} /> Diario — 09:00 AM (Lima)
                  </span>
                </div>
              </div>
              <button
                onClick={() => setGeneralConfig(prev => ({ ...prev, daily_notifications_activo: !prev.daily_notifications_activo }))}
                className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
                  generalConfig.daily_notifications_activo ? 'bg-[#4E7A79]' : 'bg-gray-300'
                }`}
                data-testid="switch-daily-notifications"
              >
                <span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  generalConfig.daily_notifications_activo ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>

          {/* Job 2: stand_by_check */}
          <div className="border border-[#E8E8E7] rounded-lg p-4" data-testid="job-stand-by-check">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-[#2D2D2D]">Verificación Stand By Empresa</h4>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  A medianoche, verifica requerimientos en Stand By Empresa cuya fecha de inicio de trabajos sea hoy. Los transiciona automáticamente a Atención Pendiente y notifica al supervisor.
                </p>
                <p className="text-[10px] text-gray-400 mt-1 italic">
                  La limpieza de URLs públicas expiradas de actas se ejecuta siempre, independientemente de este switch.
                </p>
                <div className="flex flex-wrap gap-3 mt-2">
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#888] bg-[#F5F5F5] rounded px-2 py-0.5">
                    <span className="font-medium text-[#5A5A5A]">Job ID:</span> stand_by_check
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#888] bg-[#F5F5F5] rounded px-2 py-0.5">
                    <Clock size={10} /> Diario — 00:00 medianoche (Lima)
                  </span>
                </div>
              </div>
              <button
                onClick={() => setGeneralConfig(prev => ({ ...prev, stand_by_check_activo: !prev.stand_by_check_activo }))}
                className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
                  generalConfig.stand_by_check_activo ? 'bg-[#4E7A79]' : 'bg-gray-300'
                }`}
                data-testid="switch-stand-by-check"
              >
                <span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  generalConfig.stand_by_check_activo ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>

          {/* Save button */}
          <div className="flex justify-end pt-2">
            <Button
              size="sm"
              onClick={handleSaveNotifInternas}
              disabled={savingNotifInternas}
              className="bg-[#4E7A79] hover:bg-[#3d6362] text-white"
              data-testid="btn-save-notif-internas"
            >
              {savingNotifInternas ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
              Guardar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sección 4: Configuración de Notificaciones a Contratistas */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm">
        <CardHeader className="px-3 sm:px-6">
          <div className="flex items-center gap-2">
            <Send size={18} className="text-[#9d8272] flex-shrink-0" />
            <CardTitle className="text-sm sm:text-lg font-semibold">Notificaciones a Contratistas</CardTitle>
            <Badge 
              className={`ml-auto ${notificacionesConfig.notificar_contratistas_auto ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
            >
              {notificacionesConfig.notificar_contratistas_auto ? 'Automático' : 'Manual'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          <p className="text-xs sm:text-sm text-[#888888] mb-4">
            Configura cómo se notificará a los contratistas cuando se envíe una inspección con proveedores asignados.
          </p>
          
          <div className="space-y-4">
            {/* Switch de notificación automática */}
            <div className={`flex items-center gap-3 p-3 rounded-lg border bg-[#FAFAFA] border-[#E8E8E7]`}>
              <button
                type="button"
                onClick={() => {
                  setNotificacionesConfig(prev => ({ 
                    ...prev, 
                    notificar_contratistas_auto: !prev.notificar_contratistas_auto 
                  }));
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notificacionesConfig.notificar_contratistas_auto 
                    ? 'bg-green-500' 
                    : 'bg-gray-300'
                }`}
                data-testid="notificacion-contratistas-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notificacionesConfig.notificar_contratistas_auto ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <div className="flex-1">
                <Label className="text-sm font-medium">Notificar inspecciones automáticamente por correo a contratistas</Label>
                <p className="text-xs text-[#888888]">
                  {notificacionesConfig.notificar_contratistas_auto 
                    ? 'Al enviar una inspección, se notificará automáticamente a los contratistas asignados' 
                    : 'Los contratistas deberán ser notificados manualmente usando el botón "Notificar contratistas"'}
                </p>
              </div>
            </div>

            {/* Información adicional */}
            <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800">
                  <strong>¿Cómo funciona?</strong>
                </p>
                <ul className="text-xs text-blue-700 mt-1 list-disc list-inside space-y-1">
                  <li>Los proveedores se asignan a cada solicitud que tenga evaluación "Procede"</li>
                  <li>Solo se muestran proveedores del tipo "Operario Postventa" o "Subcontratista"</li>
                  <li>El correo incluye el Acta de Inspección en PDF como adjunto</li>
                  <li>Se envía copia a los correos secundarios del proveedor (si existen)</li>
                </ul>
              </div>
            </div>

            {/* Botón guardar */}
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSaveNotificacionesConfig}
                disabled={savingNotificaciones}
                className="bg-[#9d8272] hover:bg-[#8c756a] text-white"
                data-testid="save-notificaciones-btn"
              >
                {savingNotificaciones ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Guardar configuración
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog para Asignar Proyecto/Subdivisión */}
      <Dialog open={showAsignar} onOpenChange={setShowAsignar}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Asignar a {selectedSupervisor?.nombre?.split(' ')[0]}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4">
            <p className="text-xs sm:text-sm text-[#888888]">
              Selecciona proyectos o subdivisiones. Solo se asignan a un supervisor.
            </p>
            <div className="space-y-2 sm:space-y-3 max-h-[50vh] sm:max-h-96 overflow-y-auto">
              {proyectos.map((proyecto) => {
                const subs = subdivisiones[proyecto.id] || [];
                const proyectoCompleto = !proyecto.tiene_subdivision;
                const asignadoCompleto = isProyectoAsignado(proyecto.id, null);
                const todasSubsAsignadas = proyecto.tiene_subdivision && subs.length > 0 && 
                  subs.every(sub => isProyectoAsignado(proyecto.id, sub.id));
                
                return (
                  <div key={proyecto.id} className="border border-[#E8E8E7] rounded-lg p-2 sm:p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
                        <Building2 size={14} className="text-[#9d8272] flex-shrink-0 sm:w-4 sm:h-4" />
                        <span className="font-medium text-xs sm:text-sm truncate">{proyecto.nombre}</span>
                      </div>
                      <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                        {proyecto.tiene_subdivision && subs.length > 0 && (
                          <Button
                            size="sm"
                            variant={todasSubsAsignadas ? "secondary" : "default"}
                            disabled={todasSubsAsignadas}
                            onClick={() => handleAsignarTodo(selectedSupervisor.id, proyecto.id)}
                            className={`text-[10px] sm:text-xs h-7 px-2 sm:px-3 ${!todasSubsAsignadas ? 'bg-[#9d8272] hover:bg-[#8a7162] text-white' : ''}`}
                          >
                            {todasSubsAsignadas ? 'Asignado' : 'Todo'}
                          </Button>
                        )}
                        {proyectoCompleto && (
                          <Button
                            size="sm"
                            variant={asignadoCompleto ? "secondary" : "outline"}
                            disabled={asignadoCompleto}
                            onClick={() => handleAsignar(selectedSupervisor.id, proyecto.id)}
                            className="text-[10px] sm:text-xs h-7 px-2 sm:px-3"
                          >
                            {asignadoCompleto ? 'Asignado' : 'Asignar'}
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {proyecto.tiene_subdivision && subs.length > 0 && (
                      <div className="mt-1.5 sm:mt-2 ml-4 sm:ml-6 space-y-1">
                        {subs.map((sub) => {
                          const asignadoSub = isProyectoAsignado(proyecto.id, sub.id);
                          return (
                            <div key={sub.id} className="flex items-center justify-between py-0.5 sm:py-1 gap-2">
                              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm min-w-0 flex-1">
                                <ChevronRight size={10} className="text-[#888888] flex-shrink-0 sm:w-3 sm:h-3" />
                                <span className="truncate">{sub.nombre}</span>
                              </div>
                              <Button
                                size="sm"
                                variant={asignadoSub ? "secondary" : "outline"}
                                disabled={asignadoSub}
                                onClick={() => handleAsignar(selectedSupervisor.id, proyecto.id, sub.id)}
                                className="text-[10px] sm:text-xs h-6 sm:h-7 px-2 flex-shrink-0"
                              >
                                {asignadoSub ? 'Asignado' : 'Asignar'}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {proyecto.tiene_subdivision && subs.length === 0 && (
                      <p className="text-[10px] sm:text-xs text-[#888888] ml-4 sm:ml-6 mt-1">Sin subdivisiones</p>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setShowAsignar(false)} className="text-xs sm:text-sm">
                Cerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Importación de Asignaciones */}
      <Dialog open={showImportModal} onOpenChange={handleCloseImportModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload size={20} className="text-[#9d8272]" />
              Importar Asignaciones de Supervisores
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!importResult ? (
              <>
                {/* Selector de archivo */}
                <div className="space-y-2">
                  <Label htmlFor="import-file" className="text-sm font-medium">
                    Seleccionar archivo Excel (.xlsx)
                  </Label>
                  <Input
                    id="import-file"
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="cursor-pointer"
                    data-testid="import-file-input"
                  />
                </div>

                {/* Instrucciones */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-800 mb-2">💡 Instrucciones:</p>
                  <ul className="list-disc list-inside text-blue-700 space-y-1 text-xs">
                    <li>Exporte primero para obtener la plantilla</li>
                    <li>Modifique el supervisor para reasignar</li>
                    <li>Deje el supervisor vacío para eliminar la asignación</li>
                    <li>Agregue filas nuevas para crear asignaciones</li>
                  </ul>
                </div>

                {/* Advertencia */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <p className="text-amber-800 flex items-start gap-2">
                    <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                    <span><strong>Importante:</strong> Si hay algún error en el archivo, no se realizará ningún cambio.</span>
                  </p>
                </div>

                {/* Botones */}
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={handleCloseImportModal}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleImportAsignaciones}
                    disabled={!importFile || importLoading}
                    className="bg-[#9d8272] hover:bg-[#8a7264] text-white"
                    data-testid="confirm-import-btn"
                  >
                    {importLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        Importar
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              /* Resultado de importación */
              <div className="space-y-4">
                {importResult.success ? (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-green-800 font-medium text-lg mb-3">✅ Importación completada</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between text-green-700">
                          <span>➕ Nuevas asignaciones:</span>
                          <span className="font-semibold">{importResult.resumen?.insertadas || 0}</span>
                        </div>
                        <div className="flex justify-between text-green-700">
                          <span>🔄 Reasignaciones:</span>
                          <span className="font-semibold">{importResult.resumen?.actualizadas || 0}</span>
                        </div>
                        <div className="flex justify-between text-green-700">
                          <span>🗑️ Eliminadas:</span>
                          <span className="font-semibold">{importResult.resumen?.eliminadas || 0}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>⚪ Sin cambios:</span>
                          <span className="font-semibold">{importResult.resumen?.sin_cambios || 0}</span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-800 font-medium text-lg mb-2">❌ Error en la importación</p>
                      <p className="text-red-700 text-sm mb-3">
                        Se encontraron {importResult.total_errores || importResult.errores?.length || 0} errores. 
                        No se realizó ningún cambio.
                      </p>
                      {importResult.errores && importResult.errores.length > 0 && (
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {importResult.errores.map((err, idx) => (
                            <div key={idx} className="text-xs text-red-600 bg-red-100 p-2 rounded">
                              <span className="font-medium">Fila {err.fila}:</span> {err.error}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      Por favor corrija los errores y vuelva a intentar.
                    </p>
                  </>
                )}

                {/* Botón cerrar */}
                <div className="flex justify-end pt-2">
                  <Button onClick={handleCloseImportModal} className="bg-[#9d8272] hover:bg-[#8a7264] text-white">
                    Cerrar
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Configuracion;
